// Mock base44 client - replaces the real SDK with in-memory data
import {
  seedStudents,
  seedRosterEntries,
  seedGradeReports,
  seedTeachers,
  seedUsers,
  seedContent,
  seedSettings,
  seedConsequenceLevels,
  seedSupportTickets,
  seedExcusals,
} from "./mockData";

const uid = () => Math.random().toString(36).slice(2, 12);

// A minimal admin user that must always exist so the app can render
const ADMIN_USER = {
  id: "admin-1",
  full_name: "Chanique Steele",
  email: "csteele@iltexas.org",
  role: "admin",
  user_type: null,
  created_date: new Date("2025-08-01").toISOString(),
};

const ensureAdminUser = (db) => {
  db.User = db.User || [];
  const hasAdmin = db.User.some((u) => u.role === "admin");
  if (!hasAdmin) db.User.unshift({ ...ADMIN_USER });
  return db;
};

// Load persisted state from localStorage OR start empty (only admin user).
// Demo data can be loaded on-demand via the "Reseed Demo Data" button.
const loadOrSeed = () => {
  try {
    const stored = localStorage.getItem("eagleMockDB_v1");
    if (stored) {
      const parsed = JSON.parse(stored);
      return ensureAdminUser(parsed);
    }
  } catch (e) {}
  // Fresh start - empty DB with only the admin user
  const db = {
    Student: [],
    Teacher: [],
    User: [],
    RosterEntry: [],
    GradeReport: [],
    AttendanceLog: [],
    ExcusalSubmission: [],
    Content: [],
    Settings: [],
    ConsequenceLevel: [],
    SupportTicket: [],
    EAMissTracking: [],
    ManualAssignment: [],
    Closeout: [],
  };
  return ensureAdminUser(db);
};

// Full demo seed - only called via explicit user action (Reseed button)
export const seedFullDemoData = () => {
  const students = seedStudents();
  const teachers = seedTeachers();
  const users = seedUsers(teachers);
  const rosterEntries = seedRosterEntries(students);
  const excusals = seedExcusals(rosterEntries);
  const db = {
    Student: students,
    Teacher: teachers,
    User: users,
    RosterEntry: rosterEntries,
    GradeReport: seedGradeReports(),
    AttendanceLog: [],
    ExcusalSubmission: excusals,
    Content: seedContent(),
    Settings: seedSettings(),
    ConsequenceLevel: seedConsequenceLevels(),
    SupportTicket: seedSupportTickets(),
    EAMissTracking: [],
    ManualAssignment: [],
    Closeout: [],
  };
  const withAdmin = ensureAdminUser(db);
  localStorage.setItem("eagleMockDB_v1", JSON.stringify(withAdmin));
  window.location.reload();
};

const DB = loadOrSeed();

const persist = () => {
  try {
    localStorage.setItem("eagleMockDB_v1", JSON.stringify(DB));
  } catch (e) {}
};

// Simulated async delay
const delay = (data, ms = 100) =>
  new Promise((r) => setTimeout(() => r(JSON.parse(JSON.stringify(data))), ms));

const sortItems = (items, sortKey) => {
  if (!sortKey) return items;
  const desc = sortKey.startsWith("-");
  const key = desc ? sortKey.slice(1) : sortKey;
  return [...items].sort((a, b) => {
    const va = a[key] ?? "";
    const vb = b[key] ?? "";
    if (va < vb) return desc ? 1 : -1;
    if (va > vb) return desc ? -1 : 1;
    return 0;
  });
};

// Generic entity accessor - routes to the real backend so ALL data flows through MongoDB.
// This replaces the old localStorage-only mock so every user on every device sees the same data.
const makeEntity = (name) => {
  const api = async (path, opts = {}) => {
    const res = await fetch(`${BACKEND_URL}/api/entities/${name}${path}`, {
      credentials: "include",
      headers: opts.body ? { "Content-Type": "application/json" } : {},
      ...opts,
    });
    if (!res.ok) {
      const err = await res.json().catch(() => ({}));
      throw new Error(
        typeof err.detail === "string" ? err.detail : err.detail?.message || `HTTP ${res.status}`,
      );
    }
    return res.status === 204 ? null : res.json();
  };

  return {
    list: async (sort, limit) => {
      return await api("/list", {
        method: "POST",
        body: JSON.stringify({ sort, limit }),
      });
    },
    filter: async (criteria = {}, sort, limit) => {
      return await api("/list", {
        method: "POST",
        body: JSON.stringify({ filter: criteria, sort, limit }),
      });
    },
    get: async (id) => {
      const docs = await api("/list", {
        method: "POST",
        body: JSON.stringify({ filter: { id } }),
      });
      return (docs || [])[0] || null;
    },
    create: async (data) => await api("", { method: "POST", body: JSON.stringify(data) }),
    bulkCreate: async (items) => {
      if (!items?.length) return [];
      return await api("/bulk", {
        method: "POST",
        body: JSON.stringify({ items }),
      });
    },
    update: async (id, updates) =>
      await api(`/${encodeURIComponent(id)}`, {
        method: "PATCH",
        body: JSON.stringify(updates),
      }),
    delete: async (id) => {
      await api(`/${encodeURIComponent(id)}`, { method: "DELETE" });
      return { success: true };
    },
  };
};

// Prevent deleting the last admin user (needed for app to render)
const deleteGuard = (name, id) => {
  if (name !== "User") return true;
  const item = DB.User.find((u) => u.id === id);
  if (!item) return true;
  if (item.role === "admin") {
    const remainingAdmins = DB.User.filter((u) => u.role === "admin" && u.id !== id);
    if (remainingAdmins.length === 0) {
      console.warn("[mock] Refusing to delete the last admin user");
      return false;
    }
  }
  return true;
};

// Demo users for each role - always available to switch into
const DEMO_USERS_BY_TYPE = {
  admin: {
    id: "demo-admin",
    full_name: "Chanique Steele",
    email: "csteele@iltexas.org",
    role: "admin",
    user_type: null,
  },
  assistant_principal: {
    id: "demo-ap",
    full_name: "Dr. Marcus Reed",
    email: "mreed@iltexas.org",
    role: "user",
    user_type: "assistant_principal",
    ap_grade_cohort: "4-5",
  },
  teacher: {
    id: "demo-teacher",
    full_name: "Ms. Sarah Anderson",
    email: "sanderson@iltexas.org",
    role: "user",
    user_type: "teacher",
    grade_levels: ["6", "7", "8"],
    subjects: ["Math"],
  },
  ea_staff: {
    id: "demo-ea",
    full_name: "Maria Lopez",
    email: "mlopez@iltexas.org",
    role: "user",
    user_type: "ea_staff",
  },
  driveline: {
    id: "demo-driveline",
    full_name: "Sarah Bennett",
    email: "sbennett@iltexas.org",
    role: "user",
    user_type: "driveline",
  },
  student: {
    id: "demo-student",
    full_name: "Emma Johnson",
    email: "emma.johnson@iltexasstudent.org",
    role: "user",
    user_type: "student",
    student_id: "100001",
    grade_level: "7",
  },
};

const ensureDemoUser = (userType) => {
  const template = DEMO_USERS_BY_TYPE[userType];
  if (!template) return null;
  DB.User = DB.User || [];
  const existing = DB.User.find((u) => u.id === template.id);
  if (existing) return existing;
  const created = {
    ...template,
    created_date: new Date().toISOString(),
  };
  DB.User.unshift(created);
  persist();
  return created;
};

// Fake current user (admin by default) - dynamic getter so it survives clears
const getCurrentUser = () => {
  ensureAdminUser(DB);
  const activeType = localStorage.getItem("eagleActiveRole");
  if (activeType && DEMO_USERS_BY_TYPE[activeType]) {
    return ensureDemoUser(activeType);
  }
  return DB.User.find((u) => u.role === "admin") || DB.User[0];
};

export const AVAILABLE_ROLES = [
  { key: "admin", label: "Administrator", user_type: null, role: "admin" },
  { key: "assistant_principal", label: "Assistant Principal", user_type: "assistant_principal", role: "user" },
  { key: "teacher", label: "Teacher", user_type: "teacher", role: "user" },
  { key: "ea_staff", label: "EA Staff", user_type: "ea_staff", role: "user" },
  { key: "driveline", label: "Driveline Staff", user_type: "driveline", role: "user" },
  { key: "student", label: "Student", user_type: "student", role: "user" },
];

export const switchRole = (roleKey) => {
  if (!DEMO_USERS_BY_TYPE[roleKey]) return;
  localStorage.setItem("eagleActiveRole", roleKey);
  ensureDemoUser(roleKey);
  window.location.href = "/";
};

export const getActiveRole = () => {
  return localStorage.getItem("eagleActiveRole") || "admin";
};

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

// Cache the real authenticated user so multiple base44.auth.me() calls don't spam /api/auth/me
let _cachedRealUser = null;
let _cachedAt = 0;
const CACHE_TTL = 30_000;

const fetchRealUser = async () => {
  const now = Date.now();
  if (_cachedRealUser && now - _cachedAt < CACHE_TTL) return _cachedRealUser;
  try {
    const res = await fetch(`${BACKEND_URL}/api/auth/me`, {
      credentials: "include",
    });
    if (!res.ok) return null;
    const data = await res.json();
    _cachedRealUser = data;
    _cachedAt = now;
    return data;
  } catch (e) {
    return null;
  }
};

export const clearAuthCache = () => {
  _cachedRealUser = null;
  _cachedAt = 0;
};

// Convert backend user shape to the shape existing pages expect
const shapeUser = (backendUser) => {
  if (!backendUser) return null;
  return {
    id: backendUser.user_id,
    user_id: backendUser.user_id,
    email: backendUser.email,
    full_name: backendUser.name,
    picture: backendUser.picture,
    role: backendUser.role,
    user_type: backendUser.user_type,
    teacher_grade_level: backendUser.teacher_grade_level ?? null,
    ap_grade_cohort: backendUser.ap_grade_cohort ?? null,
    assigned_ap_email: backendUser.assigned_ap_email ?? null,
    student_id: backendUser.student_id ?? null,
    needs_password_change: backendUser.needs_password_change ?? false,
  };
};

export const base44 = {
  auth: {
    me: async () => {
      // Admin-only impersonation for previewing other roles
      const activeType = localStorage.getItem("eagleActiveRole");
      const real = await fetchRealUser();
      if (real?.role === "admin" && activeType && DEMO_USERS_BY_TYPE[activeType] && activeType !== "admin") {
        return ensureDemoUser(activeType);
      }
      return shapeUser(real);
    },
    updateMe: async (updates) => {
      // Not persisted to backend in this mock — updates apply to local session cache
      if (_cachedRealUser) Object.assign(_cachedRealUser, updates);
      return _cachedRealUser;
    },
    logout: async () => {
      try {
        await fetch(`${BACKEND_URL}/api/auth/logout`, {
          method: "POST",
          credentials: "include",
        });
      } catch (e) {}
      localStorage.removeItem("eagleActiveRole");
      clearAuthCache();
      window.location.href = "/login";
    },
    redirectToLogin: () => {
      window.location.href = "/login";
    },
  },
  entities: {
    Student: makeEntity("Student"),
    // User is routed to a dedicated backend endpoint (/api/users) — the generic
    // /api/entities/User route is intentionally blocked server-side. This shim
    // only exposes list/filter/get/update because those are the only shapes
    // callers use.
    User: (() => {
      const api = async (path, opts = {}) => {
        const res = await fetch(`${BACKEND_URL}/api/users${path}`, {
          credentials: "include",
          headers: opts.body ? { "Content-Type": "application/json" } : {},
          ...opts,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = typeof err.detail === "string" ? err.detail : err.detail?.message || `HTTP ${res.status}`;
          throw new Error(detail);
        }
        return res.status === 204 ? null : res.json();
      };
      return {
        list: async () => (await api("")) || [],
        filter: async (criteria = {}) => {
          const docs = (await api("")) || [];
          return docs.filter((d) => Object.entries(criteria).every(([k, v]) => d[k] === v));
        },
        get: async (id) => {
          const docs = (await api("")) || [];
          return docs.find((d) => d.id === id || d.user_id === id) || null;
        },
        update: async (id, updates) =>
          await api(`/${encodeURIComponent(id)}`, {
            method: "PATCH",
            body: JSON.stringify(updates),
          }),
      };
    })(),
    // Student now routes to the REAL backend (MongoDB) so bulk-imported students can log in
    Student: (() => {
      const api = async (path, opts = {}) => {
        const res = await fetch(`${BACKEND_URL}/api/students${path}`, {
          credentials: "include",
          headers: opts.body && !(opts.body instanceof FormData) ? { "Content-Type": "application/json" } : {},
          ...opts,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = typeof err.detail === "string" ? err.detail : err.detail?.message || `HTTP ${res.status}`;
          throw new Error(detail);
        }
        return res.status === 204 ? null : res.json();
      };
      // Map backend doc so callers see `id === student_id` (existing code passes .id back for update/delete)
      const mapDoc = (d) => (d ? { ...d, id: d.student_id } : d);
      return {
        list: async () => {
          const docs = await api("");
          return (docs || []).map(mapDoc);
        },
        filter: async (criteria = {}) => {
          const docs = await api("");
          return (docs || [])
            .filter((d) => Object.entries(criteria).every(([k, v]) => d[k] === v))
            .map(mapDoc);
        },
        get: async (id) => {
          const docs = await api("");
          return mapDoc((docs || []).find((d) => d.student_id === id || d.id === id) || null);
        },
        create: async (data) => mapDoc(await api("", { method: "POST", body: JSON.stringify(data) })),
        update: async (id, updates) =>
          mapDoc(
            await api(`/${encodeURIComponent(id)}`, {
              method: "PATCH",
              body: JSON.stringify(updates),
            }),
          ),
        delete: async (id) => {
          await api(`/${encodeURIComponent(id)}`, { method: "DELETE" });
          return { success: true };
        },
        bulkCreate: async (items) => {
          const results = [];
          for (const item of items) {
            try {
              results.push(mapDoc(await api("", { method: "POST", body: JSON.stringify(item) })));
            } catch (e) {
              // Skip duplicates silently
            }
          }
          return results;
        },
      };
    })(),
    RosterEntry: makeEntity("RosterEntry"),
    // Content is stored server-side so all users see the same announcements/guidelines
    Content: (() => {
      const api = async (path, opts = {}) => {
        const res = await fetch(`${BACKEND_URL}/api/content${path}`, {
          credentials: "include",
          headers: opts.body ? { "Content-Type": "application/json" } : {},
          ...opts,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          const detail = typeof err.detail === "string" ? err.detail : err.detail?.message || `HTTP ${res.status}`;
          throw new Error(detail);
        }
        return res.status === 204 ? null : res.json();
      };
      return {
        list: async () => (await api("")) || [],
        filter: async (criteria = {}) => {
          const docs = (await api("")) || [];
          return docs.filter((d) => Object.entries(criteria).every(([k, v]) => d[k] === v));
        },
        get: async (id) => {
          const docs = (await api("")) || [];
          return docs.find((d) => d.id === id || d.content_key === id) || null;
        },
        create: async (data) => await api(`/${encodeURIComponent(data.content_key)}`, {
          method: "PUT",
          body: JSON.stringify(data),
        }),
        update: async (id, updates) => {
          // Callers pass either the content_key or the doc.id. Resolve to content_key.
          const key = updates?.content_key || id;
          return await api(`/${encodeURIComponent(key)}`, {
            method: "PUT",
            body: JSON.stringify({ ...updates, content_key: key }),
          });
        },
        delete: async (id) => {
          const docs = (await api("")) || [];
          const doc = docs.find((d) => d.id === id) || { content_key: id };
          await api(`/${encodeURIComponent(doc.content_key)}`, { method: "DELETE" });
          return { success: true };
        },
      };
    })(),
    GradeReport: makeEntity("GradeReport"),
    AttendanceLog: makeEntity("AttendanceLog"),
    AttendanceScanLog: makeEntity("AttendanceScanLog"),
    ExcusalSubmission: makeEntity("ExcusalSubmission"),
    Settings: makeEntity("Settings"),
    ConsequenceLevel: makeEntity("ConsequenceLevel"),
    SupportTicket: makeEntity("SupportTicket"),
    EAMissTracking: makeEntity("EAMissTracking"),
    ManualAssignment: makeEntity("ManualAssignment"),
    Closeout: makeEntity("Closeout"),
    Teacher: makeEntity("Teacher"),
  },
  functions: {
    invoke: async (name, payload) => {
      // Map named functions to real backend endpoints
      const routes = {
        eaCloseout: "/api/closeout/ea",
        asdCloseout: "/api/closeout/asd",
      };
      const path = routes[name];
      if (!path) {
        console.log("[mock] functions.invoke", name, payload);
        return { data: { success: true }, error: null };
      }
      try {
        const res = await fetch(`${BACKEND_URL}${path}`, {
          method: "POST",
          credentials: "include",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload || {}),
        });
        const body = await res.json().catch(() => ({}));
        if (!res.ok) {
          return {
            data: null,
            error:
              typeof body.detail === "string"
                ? body.detail
                : body.detail?.message || `HTTP ${res.status}`,
          };
        }
        return { data: body, error: null };
      } catch (e) {
        return { data: null, error: e.message || "Network error" };
      }
    },
  },
  integrations: {
    Core: {
      SendEmail: async (opts) => {
        console.log("[mock] SendEmail", opts);
        return delay({ success: true });
      },
      SendSMS: async (opts) => {
        console.log("[mock] SendSMS", opts);
        return delay({ success: true });
      },
      UploadFile: async ({ file }) => {
        if (!file) return { file_url: "" };
        const fd = new FormData();
        fd.append("file", file);
        const res = await fetch(`${BACKEND_URL}/api/files`, {
          method: "POST",
          credentials: "include",
          body: fd,
        });
        if (!res.ok) {
          const err = await res.json().catch(() => ({}));
          throw new Error(err.detail || "Upload failed");
        }
        const data = await res.json();
        // Return absolute URL so it works when linked from Guidelines page
        return { ...data, file_url: `${BACKEND_URL}${data.file_url}` };
      },
      GenerateImage: async () => delay({ url: "" }),
      InvokeLLM: async () => delay({ response: "" }),
      ExtractDataFromUploadedFile: async () => delay({ data: [] }),
    },
  },
};

export const resetMockDB = () => {
  localStorage.removeItem("eagleMockDB_v1");
  window.location.reload();
};
